import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const VideoPreview = ({ src, poster, objectFit }) => {
  const { t } = useTranslation();
  const [unsupported, setUnsupported] = useState(false);

  useEffect(() => { setUnsupported(false); }, [src]);

  return (
    <>
      <video
        src={src}
        poster={poster}
        className="w-100"
        style={{ height: '250px', objectFit, objectPosition: 'center', display: 'block' }}
        controls
        preload="metadata"
        muted
        playsInline
        onError={(e) => {
          if (e.target.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
            setUnsupported(true);
          }
        }}
      />
      {unsupported && (
        <div
          className="position-absolute start-0 w-100 text-white text-center px-3"
          style={{ bottom: '60px', pointerEvents: 'none' }}
        >
          {t('video_preview_unsupported')}
        </div>
      )}
    </>
  );
};

const PostMediaEditor = ({
  images = [],
  onDeleteImage,
  onEditMedia,
  mode = 'create',
  disabled = false,
  containerClassName = "",
  singleImageMode = false
}) => {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // 圖片導航函數 - 一次滑動一張，支援循環顯示
  const goToPrevGroup = () => {
    setCurrentImageIndex(prev => {
      if (prev === 0) {
        return images.length - 1;
      }
      return prev - 1;
    });
  };

  const goToNextGroup = () => {
    setCurrentImageIndex(prev => {
      if (prev >= images.length - 1) {
        return 0;
      }
      return prev + 1;
    });
  };

  // 當圖片數組變化時重置索引
  const resetImageIndex = () => {
    if (currentImageIndex >= images.length) {
      setCurrentImageIndex(Math.max(0, images.length - 1));
    }
  };

  // 監聽圖片數組變化，自動調整當前索引
  useEffect(() => {
    resetImageIndex();
  }, [images.length]);

  // keep-mount：所有 media 都 render 在 DOM 內，CSS 控制可見的兩張（單張模式只 1 張）。
  // 避免 <video> 反覆 unmount/mount 造成切換時的黑塊與 metadata 重載。
  const isSingleLayout = singleImageMode || images.length === 1;
  const colClass = isSingleLayout ? 'col-12' : 'col-6';
  const objectFitValue = isSingleLayout ? 'contain' : 'cover';
  const visibleCount = isSingleLayout ? 1 : Math.min(2, images.length);

  const handleDelete = (e, actualIndex) => {
    e.stopPropagation();
    if (mode === 'edit') {
      onDeleteImage(e, undefined, actualIndex);
    } else {
      onDeleteImage(actualIndex);
    }
  };

  return (
    <div className={`${containerClassName}`}>
      {images.length > 0 && (
        <div className="row mt-2 mx-0 g-1">
          <div className="position-relative">
            <div className="row mx-0 g-1">
              {images.map((image, actualIndex) => {
                const offset = (actualIndex - currentImageIndex + images.length) % images.length;
                const visible = offset < visibleCount;
                return (
                  <div
                    key={actualIndex}
                    className={`${colClass} position-relative`}
                    style={{
                      display: visible ? undefined : 'none',
                      order: visible ? offset : 999,
                    }}
                  >
                    {image.type === 1 ? (
                      image.uploading ? (
                        <div
                          className="w-100 d-flex flex-column align-items-center justify-content-center bg-dark"
                          style={{ height: '250px' }}
                        >
                          <div className="spinner-border text-white mb-2" role="status" />
                          <span className="text-white fs-12">{t('video_uploading')}</span>
                        </div>
                      ) : image.playback_url ? (
                        // 編輯既有貼文：CF Stream 已上線影片，用 thumbnail 立即顯示
                        <img
                          src={image.thumbnail_url}
                          alt="Video thumbnail"
                          className="w-100"
                          style={{
                            height: '250px',
                            objectFit: objectFitValue,
                            objectPosition: 'center',
                            display: 'block',
                            backgroundColor: '#000'
                          }}
                        />
                      ) : (
                        // 新上傳影片：用本機 blob URL 預覽
                        <VideoPreview
                          src={image.url}
                          poster={image.thumbnail_url}
                          objectFit={objectFitValue}
                        />
                      )
                    ) : (
                      <img
                        src={image.url}
                        alt="Media"
                        className="w-100"
                        style={{ height: '250px', objectFit: objectFitValue, objectPosition: 'center', display: 'block' }}
                      />
                    )}

                    {!disabled && (
                      <>
                        {!isSingleLayout && (
                          <div
                            className="position-absolute bg-dark text-white px-2 py-1 rounded"
                            style={{ top: '10px', left: '8px', fontSize: '12px', zIndex: 10 }}
                          >
                            {actualIndex + 1}/{images.length}
                          </div>
                        )}

                        <div
                          className="position-absolute d-flex align-items-center gap-1"
                          style={{ top: '8px', right: '8px', zIndex: 10 }}
                        >
                          <button
                            className="btn btn-dark rounded-pill fs-12"
                            onClick={() => onEditMedia(actualIndex)}
                          >
                            {t('edit')}
                          </button>
                          <FontAwesomeIcon
                            icon="fa-solid fa-circle-xmark"
                            className="fs-3 bg-white rounded-pill cursor-p"
                            onClick={(e) => handleDelete(e, actualIndex)}
                          />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 導航按鈕 - 只在有超過2張圖片且非單張模式時顯示 */}
            {!disabled && !singleImageMode && images.length > 2 && (
              <>
                {/* 左導航按鈕 */}
                <button
                  className="btn btn-dark position-absolute rounded-circle"
                  style={{
                    top: '50%',
                    left: '8px',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={goToPrevGroup}
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="fs-12" />
                </button>

                {/* 右導航按鈕 */}
                <button
                  className="btn btn-dark position-absolute rounded-circle"
                  style={{
                    top: '50%',
                    right: '8px',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={goToNextGroup}
                >
                  <FontAwesomeIcon icon={faChevronRight} className="fs-12" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PostMediaEditor;